import { Organization, type OrganizationRepository } from '@telespot/domain';

import { ParseBaseRepository } from '../../parse-base.repository';

import { AffiliateTopology } from '../affiliate/parse-affiliate.mapper';
import { ParseOrganizationMapper, OrganizationTopology } from './parse-organization.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';

export class ParseOrganizationRepository extends ParseBaseRepository implements OrganizationRepository {
  private readonly orgMapper = new ParseOrganizationMapper(this.parse);

  public async list(
    searchTerm?: string,
    limit?: number,
    lastOrganizationName?: string,
    userId?: string
  ): Promise<Organization[]> {
    const query = new this.parse.Query(OrganizationTopology.TABLE).ascending(OrganizationTopology.NAME);

    // Filter by user affiliations if userId is provided
    if (userId) {
      const user = this.parse.User.createWithoutData(userId);
      const affiliateQuery = new this.parse.Query(AffiliateTopology.TABLE).equalTo(AffiliateTopology.USER, user);
      query.matchesKeyInQuery(
        ObjectTopology.ID,
        `${AffiliateTopology.ORGANIZATION}.${ObjectTopology.ID}`,
        affiliateQuery
      );
    }

    if (searchTerm) query.matches(OrganizationTopology.NAME, new RegExp(searchTerm, 'i'));
    if (lastOrganizationName) query.greaterThan(OrganizationTopology.NAME, lastOrganizationName);
    if (limit) query.limit(limit);

    const organizations = await query.find(this.options);
    const domainOrgs = organizations.map((parseOrganization) => this.orgMapper.toDomain(parseOrganization, 0));
    return domainOrgs;
  }

  public async count(searchTerm?: string, userId?: string): Promise<number> {
    const query = new this.parse.Query(OrganizationTopology.TABLE);

    // Filter by user affiliations if userId is provided
    if (userId) {
      const user = this.parse.User.createWithoutData(userId);
      const affiliateQuery = new this.parse.Query(AffiliateTopology.TABLE).equalTo(AffiliateTopology.USER, user);
      query.matchesKeyInQuery(
        ObjectTopology.ID,
        `${AffiliateTopology.ORGANIZATION}.${ObjectTopology.ID}`,
        affiliateQuery
      );
    }

    if (searchTerm) query.matches(OrganizationTopology.NAME, new RegExp(searchTerm, 'i'));
    return query.count(this.options);
  }

  public async save(organization: Organization, logo?: File): Promise<string> {
    const parseOrganization = this.orgMapper.fromDomain(organization, logo);
    const { id } = await parseOrganization.save(null, this.options);

    // Manage logo saving
    const parseLogo = parseOrganization.get(OrganizationTopology.LOGO) as Parse.File;
    if (parseLogo) {
      await parseLogo.save();
      organization.changeLogo(parseLogo.name(), parseLogo.url());
    }

    // Return identifier of the saved organization
    return id;
  }

  public async delete(organizationId: string): Promise<void> {
    try {
      const organization = this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId);
      await organization.save(
        {
          [SoftDeletableTopology.DELETED_AT]: new Date(),
        },
        this.options
      );
    } catch (error) {
      switch (error.code) {
        case 101: // Object not found
          return; // organization not found, that's ok
        default:
          throw error;
      }
    }
  }

  public async getById(id: string): Promise<Organization> {
    const query = new this.parse.Query(OrganizationTopology.TABLE);
    query.doesNotExist(SoftDeletableTopology.DELETED_AT);

    const organization = await query.get(id, this.options);

    const memberCount = await new this.parse.Query(AffiliateTopology.TABLE)
      .equalTo(AffiliateTopology.ORGANIZATION, organization)
      .count(this.options);

    return this.orgMapper.toDomain(organization, memberCount);
  }
}
